import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import useVoyagerStore from '../../stores/useVoyagerStore';
import type { LandingAgentTurn } from '../../stores/slices/landingAgentSlice';

/** A settled delivery note (#1500 flag on): said in the answer's panel, with the inline instance's Retry. */
export interface DeliveryNote { message: string; retry: (() => void) | null }

export default function AnswerDeliveryStatus({ turn, onSettledNote }: {
  turn?: LandingAgentTurn;
  /** flag on: a settled note (unavailable, failed, gone) is handed up for the panel instead of shown here; the
   *  in-progress lines stay inline */
  onSettledNote?: (note: DeliveryNote | null) => void;
}) {
  const recover = useVoyagerStore((s) => s.recoverLandingAgentAnswer);
  const turnId = turn?.id;
  const sessionId = turn?.sessionId;
  const ladder = turn?.result?.ladder;
  const status = ladder?.delivery?.context_status;
  const hasLadder = !!ladder;
  const [retry, setRetry] = React.useState(0);
  const [failed, setFailed] = React.useState(false);
  const [missing, setMissing] = React.useState(false);
  const eligible = turn?.status === 'done' && !!turn.sessionId && !turn.result?.diagnose;
  React.useEffect(() => {
    if (!eligible || !turnId || !sessionId || status === 'ready' || (status === 'unavailable' && !retry) || (hasLadder && !status)) return;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    let attempts = 0;
    let inFlight = false;
    setFailed(false);
    setMissing(false);
    const deadline = setTimeout(() => { controller.abort(); setFailed(true); }, 150000);
    const poll = async () => {
      if (inFlight || controller.signal.aborted) return;
      inFlight = true;
      const outcome = await recover(turnId, sessionId, controller.signal);
      inFlight = false;
      if (controller.signal.aborted) return;
      if (outcome === 'not_found') { clearTimeout(deadline); setMissing(true); return; }
      if (outcome === 'done') { clearTimeout(deadline); return; }
      if (outcome === 'failed' && ++attempts >= 20) { clearTimeout(deadline); setFailed(true); return; }
      timer = setTimeout(poll, 2000);
    };
    void poll();
    const reconnect = () => setRetry((n) => n + 1);
    window.addEventListener('online', reconnect);
    return () => { controller.abort(); clearTimeout(timer); clearTimeout(deadline); window.removeEventListener('online', reconnect); };
  }, [eligible, turnId, sessionId, status, hasLadder, retry, recover]);
  const onlySignificanceUnavailable = status === 'unavailable' && !!ladder?.context
    && Object.entries(ladder.delivery?.checks || {}).every(([name, outcome]) => outcome === 'ready' || (name === 'significance' && outcome === 'unavailable'));
  const hidden = !eligible || status === 'ready' || (hasLadder && !status) || onlySignificanceUnavailable;
  const settled = !hidden && (missing || failed || status === 'unavailable');
  const message = missing ? 'Additional details are no longer available for this answer.' : status === 'unavailable' ? 'Some additional context checks are unavailable. The answer is still available.'
    : failed ? 'Could not retrieve all answer details. The displayed answer is still available.'
      : status === 'pending' ? 'Checking additional context…' : 'Retrieving number actions and context…';
  const canRetry = !missing && (failed || status === 'unavailable');
  const retryNow = React.useCallback(() => setRetry((n) => n + 1), []);
  React.useEffect(() => {
    if (!onSettledNote) return;
    onSettledNote(settled ? { message, retry: canRetry ? retryNow : null } : null);
  }, [onSettledNote, settled, message, canRetry, retryNow]);
  if (hidden || (onSettledNote && settled)) return null;
  return <Box role="status" sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary">{message}</Typography>
    {canRetry && <Button size="small" onClick={retryNow}>Retry loading details</Button>}
  </Box>;
}
